import React from 'react';
import { getAgentAssets } from '../config/agentAssets';
import { useAssistantTranslation } from '../contexts/AssistantContext';

export interface AgentIconProps {
  /** Agent kit name (e.g., 'payouts.assistant.payouts_info_agent') */
  agentKitName: string;
  /** Optional width override */
  width?: number | string;
  /** Optional height override */
  height?: number | string;
  /** Optional CSS class */
  className?: string;
  /** Optional alt text override */
  alt?: string;
  /** Optional inline styles */
  style?: React.CSSProperties;
}

/**
 * Agent Icon Component
 *
 * Replaces all individual icon components (PayoutsInfoIcon, InquiriesIcon, etc.)
 * Uses configuration-driven asset loading for maintainability.
 *
 * @example
 * <AgentIcon agentKitName="payouts.assistant.payouts_info_agent" />
 * <AgentIcon agentKitName="disputes.inquiries" width={32} height={32} />
 */
export const AgentIcon: React.FC<AgentIconProps> = ({
  agentKitName,
  width,
  height,
  className,
  alt,
  style = {},
  ...props
}) => {
  const { t } = useAssistantTranslation();

  // Get asset configuration for this agent
  const assetConfig = getAgentAssets(agentKitName);

  if (!assetConfig) {
    console.error(`No asset configuration found for agent: ${agentKitName}`);
    return (
      <div
        className={className}
        style={{
          width: width || 40,
          height: height || 40,
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          fontSize: '12px',
          ...style,
        }}
      >
        ?
      </div>
    );
  }

  // Use provided dimensions or fall back to asset config defaults
  const finalWidth = width ?? assetConfig.defaultIconSize?.width ?? 40;
  const finalHeight = height ?? assetConfig.defaultIconSize?.height ?? 40;

  // Use provided alt text or translate from config
  const altText = alt || `${t(assetConfig.translationKey, assetConfig.altText)} Icon`;

  return (
    <img
      alt={altText}
      className={className}
      height={finalHeight}
      src={assetConfig.icon}
      style={{
        borderRadius: '8px',
        ...style,
      }}
      width={finalWidth}
      {...props}
    />
  );
};

export default AgentIcon;
